import React from 'react';
import PropTypes from 'prop-types';
import {DataTable} from '@pepsico-ds/ui/Organisms';
import {usePeacockDataTable} from './hooks/usePeacockDataTable-hook';
import ToolbarActionsRight from './ToolbarActionsRight';
import './PeacockDataTable.scss';

/**
 * Reusable server-side-paginated table on the Peacock (@pepsico-ds/ui)
 * DataTable. Every feature is opt-in via grouped config props; full reference
 * in docs/peacock-datatable-usage.md.
 *
 * This component is a thin render layer — all business logic lives in
 * usePeacockDataTable (hooks/usePeacockDataTable-hook.js).
 */

const PeacockDataTable = ({
  columns = [],
  records = [],
  isLoading = false,
  isFetching = false,
  pageSize = 10,
  totalRecords = 0,
  tableHeader = '',
  showTableHeader = false,
  paginationConfig = {},
  selectionConfig = {},
  sortingConfig = {},
  filterConfig = {},
  toolbarConfig = {},
  displayConfig = {}
}) => {
  const {
    peacockColumns,
    handleSortingChange,
    handleColumnFiltersChange,
    handlePageIndexChange,
    handleRowsPerPageChange,
    handleTableReady,
    handleRowSelectionUpdate,
    selectedRowsActions,
    peacockToolbarActions,
    rightSideActionButton,
    actionMenuConfig,
    resetFilterOptions,
    selectionMode,
    wrapperClasses,
    selectAllSummary,
    pageIndex,
    hidePagination,
    hideFilterRow,
    hideToolbar,
    showFilterButton,
    showMultiSortMenu,
    noDataTitle,
    noDataDescription,
    tableSize,
    maxHeight
  } = usePeacockDataTable({
    columns,
    records,
    totalRecords,
    paginationConfig,
    selectionConfig,
    sortingConfig,
    filterConfig,
    toolbarConfig,
    displayConfig
  });

  return (
    <div
      className={wrapperClasses}
      style={
        selectAllSummary
          ? {
              // Escaped for use as a CSS string value.
              '--select-all-summary': `"${String(selectAllSummary).replace(/"/g, '\\"')}"`
            }
          : undefined
      }>
      <ToolbarActionsRight
        actions={rightSideActionButton}
        actionMenuConfig={actionMenuConfig}
      />
      {isFetching && !isLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            pointerEvents: 'none',
            backgroundColor: 'rgba(0,0,0,0.05)'
          }}
        />
      )}
      <DataTable
        columns={peacockColumns}
        data={records}
        isLoading={isLoading}
        tableHeader={tableHeader}
        showTableHeader={showTableHeader && !!tableHeader}
        noDataTitleText={noDataTitle}
        noDataDescriptionText={noDataDescription}
        size={tableSize}
        maxHeight={maxHeight}
        enableSorting={true}
        showColumnFilters={!hideFilterRow}
        showFilterToolbar={!hideToolbar}
        showFilterButton={showFilterButton}
        showMultiSortMenu={showMultiSortMenu}
        selection={selectionMode}
        manualPagination={!hidePagination}
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalRowsCount={totalRecords}
        onPageIndexChange={handlePageIndexChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        manualSorting={true}
        sortingHandler={handleSortingChange}
        manualFiltering={true}
        onColumnFiltersChange={handleColumnFiltersChange}
        rowSelectionStateUpdater={handleRowSelectionUpdate}
        selectedRowsActions={
          selectedRowsActions.length > 0 ? selectedRowsActions : undefined
        }
        toolbarActions={peacockToolbarActions}
        resetFilterButtonOptions={resetFilterOptions}
        onTableReady={handleTableReady}
      />
    </div>
  );
};

PeacockDataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  records: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  pageSize: PropTypes.number,
  totalRecords: PropTypes.number,
  tableHeader: PropTypes.string,
  showTableHeader: PropTypes.bool,
  paginationConfig: PropTypes.shape({
    pageIndex: PropTypes.number,
    setPageIndex: PropTypes.func,
    hidePagination: PropTypes.bool,
    onRowsPerPageChange: PropTypes.func
  }),
  selectionConfig: PropTypes.shape({
    onRowSelect: PropTypes.func,
    hideCheckbox: PropTypes.bool,
    enableSelectAllPages: PropTypes.bool,
    formatSelectAllSummary: PropTypes.func
  }),
  sortingConfig: PropTypes.shape({
    handleSorting: PropTypes.func
  }),
  filterConfig: PropTypes.shape({
    setSearchArry: PropTypes.func,
    onResetAll: PropTypes.func,
    hideFilterRow: PropTypes.bool,
    showResetAll: PropTypes.bool,
    resetAllPosition: PropTypes.oneOf(['left', 'right']),
    resetAllText: PropTypes.string,
    disabledHeaders: PropTypes.arrayOf(PropTypes.string)
  }),
  toolbarConfig: PropTypes.shape({
    hideToolbar: PropTypes.bool,
    toolbarActions: PropTypes.arrayOf(PropTypes.object),
    showDownload: PropTypes.bool,
    onDownload: PropTypes.func,
    downloadText: PropTypes.string,
    showCreateNew: PropTypes.bool,
    onCreateNew: PropTypes.func,
    createNewText: PropTypes.string,
    showFilterButton: PropTypes.bool,
    showMultiSortMenu: PropTypes.bool,
    showActionMenu: PropTypes.bool,
    actionItems: PropTypes.arrayOf(PropTypes.object),
    onActionClick: PropTypes.func
  }),
  displayConfig: PropTypes.shape({
    wrapperClassName: PropTypes.string,
    noDataTitle: PropTypes.string,
    noDataDescription: PropTypes.string,
    tableSize: PropTypes.oneOf(['large', 'medium', 'small']),
    maxHeight: PropTypes.string
  })
};

PeacockDataTable.displayName = 'PeacockDataTable';

export default PeacockDataTable;
